/**
 * The prefill catalog.
 *
 * One place describes every value Salesforce can hand the technician, so a schema references a
 * stable key (`kiosk.layout`) instead of restating an API name it might get wrong. Every entry
 * carries the API name, a confidence, and the evidence for it.
 *
 * Fill rates are the measured figures from `discovery/salesforce/kiosk-payload-spec.md`
 * (read-only describes plus counts against the `accesspoints` production org, 56,919 InService
 * kiosks). They are here because they decide UI: a 10%-filled field must render as absent
 * gracefully, and a 100%-filled field can be relied on to gate display logic.
 *
 * Field API names are cross-checked against `discovery/salesforce/field-dumps.txt` and the
 * kiosk read projection in `infra/src/salesforce/salesforce-reader.ts`, which is the set the
 * backend actually selects today. A key whose mapping is `unverified` has not been seen in
 * either and must not be treated as safe.
 */

import type { Answer, PrefillSourceSpec } from './types.ts';

const KIOSK_READ = 'infra/src/salesforce/salesforce-reader.ts kiosk read projection';
const FIELD_DUMP = 'discovery/salesforce/field-dumps.txt (OpportunityLineItem describe)';
const PAYLOAD_SPEC = 'discovery/salesforce/kiosk-payload-spec.md';

function kioskField(
  key: string,
  label: string,
  field: string,
  type: string,
  fill: number,
  extra: Partial<PrefillSourceSpec> = {},
): PrefillSourceSpec {
  return {
    key,
    label,
    fill,
    mapping: {
      object: 'OpportunityLineItem',
      field,
      type,
      confidence: 'verified',
      evidence: `${KIOSK_READ}; ${FIELD_DUMP}; fill measured in ${PAYLOAD_SPEC}`,
    },
    ...extra,
  };
}

/**
 * The catalog.
 *
 * Ordered roughly by how load-bearing each value is for removing questions. The first eleven
 * are the parity set Qualtrics already passes as URL parameters today and then asks the
 * technician to retype anyway.
 */
export const PREFILL_SOURCES: readonly PrefillSourceSpec[] = [
  /* ---- identity and clock: nobody asserts these, the device does ---- */
  {
    key: 'tech.name',
    label: 'Signed-in technician (verified token)',
    // Deliberately unmapped. `InstallersTech_Name__c` and `InstallersTech_email__c` exist but
    // are excluded from the write path by decision: identity comes from the verified token and
    // the backend sets `Submitted_By_Email__c` server-side. A client-asserted technician name
    // is not evidence of anything.
  },
  {
    key: 'visit.startTime',
    label: 'Device clock at first answer',
    mapping: {
      object: 'Work_Submission__c',
      field: 'Visit_Start_Time__c',
      type: 'string(30)',
      confidence: 'verified',
      evidence: 'discovery/salesforce/field-dumps.txt; Maintenance workflow mapping in discovery/qualtrics/10-WORKFLOWS-AND-SF-MAPPING.md',
      caveat: 'string(30), not a DateTime. Send "HH:MM" as the forms do today, not an ISO instant.',
    },
  },

  /* ---- the eleven parity fields ---- */
  kioskField('kiosk.id', 'Kiosk ID on the dispatch', 'Kiosk_ID__c', 'string(10) externalId+idLookup', 1.0),
  kioskField('kiosk.name', 'Locker name on the record', 'Kiosk_Name__c', 'string(15)', 1.0, {
    // Not Locker_Name__c: unique=true but only 2.6% populated. Reproducing the Qualtrics
    // parameter name literally would leave this blank 97% of the time.
  }),
  kioskField('kiosk.layout', 'Module layout string', 'Configuration_Order__c', 'textarea(255)', 1.0),
  kioskField('kiosk.generation', 'Locker generation', 'Locker_Generation__c', 'picklist', 1.0),
  kioskField('kiosk.color', 'Locker colour', 'Kiosk_Color__c', 'picklist', 0.905),
  kioskField('kiosk.placement', 'Indoor or outdoor', 'Environment_Type__c', 'picklist Indoor|Outdoor', 0.999),
  kioskField('kiosk.mountType', 'How it is mounted', 'Location_Type__c', 'picklist', 0.997),
  kioskField('kiosk.accessPointType', 'Product line (drives CORE vs ODIN)', 'Access_Point_Type__c', 'picklist', 1.0),
  kioskField('kiosk.countryCode', 'Country', 'Country_Code__c', 'string(10) ISO-2', 1.0),
  kioskField('kiosk.siteName', 'Site / host name', 'Address_Line_1__c', 'string(255)', 1.0),
  kioskField('kiosk.street', 'Street address', 'Address_Line_2__c', 'string(255)', 0.929),

  /* ---- derived ---- */
  {
    key: 'kiosk.moduleCount',
    label: 'Module count, derived from Hub_Size__c',
    fill: 1.0,
    mapping: {
      object: 'OpportunityLineItem',
      field: 'Hub_Size__c',
      type: 'double (formula)',
      confidence: 'verified',
      evidence: `${KIOSK_READ}; derivation ceil(Hub_Size__c / 3) verified on 7,800 kiosks with zero outliers in ${PAYLOAD_SPEC}`,
      caveat: 'Derived, not written back. Hub_Size__c is in feet and already encodes the Gen 5 two-columns-per-module rule.',
    },
  },

  /* ---- context the technician has never had before ---- */
  kioskField('kiosk.directions', 'Customer-facing directions', 'Friendly_Directions_Long__c', 'textarea(131072)', 1.0, {
    // Not Friendly_Directions__c — labelled z_… and 34% filled. Already localised on the record.
  }),
  kioskField('kiosk.accessMechanism', 'Public or restricted access', 'accessMechanisms__c', 'picklist Public|Restricted', 0.914),
  kioskField('kiosk.contactMobile', 'On-site contact mobile', 'onsite_Contact_mobile__c', 'string(100)', 0.976),
  kioskField('kiosk.contactEmail', 'On-site contact email', 'On_site_Contact_email__c', 'string(100)', 0.947),
  kioskField('kiosk.macAddress', 'Modem MAC on the record', 'Mac_Address__c', 'string(255)', 0.784),
  kioskField('kiosk.status', 'Kiosk status', 'Kiosk_Status__c', 'string(1300) formula', 1.0),
  kioskField('kiosk.installGuideLink', 'Install guide', 'Install_Guide_Link__c', 'url(255)', 0.561, {
    // Third-party hosts, some plain http:// — will be blocked as mixed content. Link, never embed.
  }),
  kioskField('kiosk.placementInstructions', 'Placement instructions', 'Placement_Instructions__c', 'textarea(255)', 0.107),
  kioskField('kiosk.floorType', 'Floor type', 'Floor_Type__c', 'string', 0.0),
  {
    key: 'kiosk.totalSlots',
    label: 'Slot count',
    fill: 0.997,
    trusted: false,
    mapping: {
      object: 'OpportunityLineItem',
      field: 'Total_Slots__c',
      type: 'double',
      confidence: 'verified',
      evidence: `${KIOSK_READ}; zero-value defect verified in ${PAYLOAD_SPEC}`,
      caveat: 'Verified reading 0 on a live Gen-4 kiosk with 23 real slots. Untrusted: shown for cross-check, never adopted as an answer.',
    },
  },

  /* ---- intervention / case ----------------------------------------------------------------
   *
   * ⚠️ EVERY `fill` BELOW WAS RE-MEASURED AGAINST DEV1 ON 2026-09-03 and several were wrong.
   *
   * Method: SOQL `SELECT COUNT(Id) FROM Intervention__c WHERE <field> != null`, run twice — once
   * over all **185,591** rows and once over the **25,080** rows the work-list predicate admits
   * (`Vendor_State__c IN (New, Scheduled, In progress, Rework, Corrective Action, Completed)`).
   * **The figures recorded here are the ACTIONABLE ones**, because that is the population a
   * technician's queue is drawn from; measuring over all 185,591 mixes in 65,878 `Cancelled` rows
   * nobody will ever open.
   *
   * What changed, and each was a claim this app would have acted on:
   *
   *   · `intervention.dispatchReason` said **1.0**. Measured **0.549** — and **0.0** on
   *     Survey-type rows. A `single`-kind question prefilled from a field that is blank 45% of
   *     the time is still a question, and stating otherwise inflated the "questions removed" count.
   *   · `intervention.supplierWorkOrderId` had no `fill` at all. Measured **0.0018** — 44 rows of
   *     25,080. This is the field the install form's "External ID / installation ID" question is
   *     bound to, so that question was never going to be answered by the record.
   *   · `intervention.visitKind` said **1.0** on a derivation that needs
   *     `Provisioned_With_Installation__c`, measured `true` on **0 of 185,591** rows.
   *   · `intervention.warehouse` said `unverified`. `Ware_House_Location__c` **does** exist
   *     (reference to `Location`), so the API name is now verified; its fill was 0.016 and is
   *     **0.0905** on the actionable set.
   *
   * ⚠️ ONE CAVEAT ON ALL OF IT. Dev1 is a sandbox. A fill rate reflects the data as it was copied,
   * and a field written only by a production integration can read low here for a reason that has
   * nothing to do with the field. `Supplier_WorkOrderId__c` at 0.18% is the one figure where that
   * genuinely matters and it was NOT cross-checked against production (production is read-only in
   * this project and the measurement brief said Dev1). Treat 0.18% as "measured in Dev1", not as
   * "true of production".
   */
  {
    key: 'intervention.id',
    label: 'Intervention on the dispatch',
    fill: 1.0,
    mapping: {
      object: 'Intervention__c',
      field: 'Id',
      type: 'Id (prefix a4Z)',
      confidence: 'verified',
      evidence: 'discovery/salesforce/field-dumps.txt; intervention read projection in infra/src/salesforce/salesforce-reader.ts',
    },
  },
  {
    key: 'intervention.locationName',
    label: 'Location on the dispatch',
    fill: 0.9857,
    mapping: {
      object: 'Intervention__c',
      field: 'Account_Name__c',
      type: 'string(1300) formula',
      confidence: 'verified',
      evidence:
        'Formula read from the Dev1 describe: IF(ISBLANK(Post_Install__c), Case__r.Account.Name, Post_Install__r.Account__r.Name). Fill measured 24,722/25,080 = 98.57% on the actionable set and 2,204/2,272 = 97.0% on Survey-type rows (Dev1, 2026-09-03). Sample values: "West Hartford Collection", "Penny - WIEN - EMICHGASSE 6", "GALP - Betxi".',
      caveat:
        'The HOST ACCOUNT name, not a street address — which is what the question "name of the location" asks for. Distinct from kiosk.siteName, which reads OpportunityLineItem.Address_Line_1__c. This one is the only source on a survey: 50.7% of Survey-type interventions have no Kiosk_ID__c, so there is no kiosk record to read.',
    },
  },
  {
    key: 'intervention.installationId',
    label: 'Installation ID on the dispatch',
    fill: 0.9893,
    mapping: {
      object: 'Intervention__c',
      field: 'Installation_Id__c',
      type: 'string(1300) formula',
      confidence: 'verified',
      evidence:
        'Formula read from the Dev1 describe: IF(Case__c != NULL, Case__r.Installation_Id__c, Post_Install__r.Installation_ID__c). Fill measured 24,811/25,080 = 98.93% on the actionable set (Dev1, 2026-09-03) — the best-filled identifier on the object. Sample values: "AP343187343187", "GA339526339526".',
      caveat:
        '⚠️ BOUND TO NO FORM FIELD ON PURPOSE. The only question whose label mentions an installation id is the install form\'s "External ID / installation ID", and that question writes to Vendor_reference_number__c — the VENDOR\'s reference number, on the write allowlist. Installation_Id__c is Amazon\'s id. Prefilling it there would put one namespace in the other\'s field and make Vendor_reference_number__c unqueryable, so the value is carried and displayed but not adopted. Splitting that question into two is an owner decision, not a mapping fix.',
    },
  },
  {
    key: 'intervention.postInstallId',
    label: 'Post Install ID (PIID)',
    fill: 0.7238,
    mapping: {
      object: 'Intervention__c',
      field: 'Post_Install__c',
      type: 'reference to Sim_Ticket__c (prefix a4Y)',
      confidence: 'verified',
      evidence:
        'discovery/salesforce/field-dumps.txt; discovery/qualtrics/10-WORKFLOWS-AND-SF-MAPPING.md "How Post Install is actually modelled". Fill re-measured 18,153/25,080 = 72.38% on the actionable set (Dev1, 2026-09-03), which corroborates the 0.718 previously recorded.',
    },
  },
  {
    key: 'intervention.supplierWorkOrderId',
    label: 'Supplier work-order reference',
    fill: 0.0018,
    mapping: {
      object: 'Intervention__c',
      field: 'Supplier_WorkOrderId__c',
      type: 'string(255) unique',
      confidence: 'verified',
      evidence:
        'discovery/salesforce/field-dumps.txt; also used as an idempotency key in infra/src/writer. Fill measured 44/25,080 = 0.18% on the actionable set and 113/185,591 = 0.06% overall (Dev1, 2026-09-03).',
      caveat:
        '⚠️ 0.18% FILLED. This is the semantically correct field for "the vendor\'s own reference number" and it is empty on 99.8% of live jobs, so binding a question to it removes that question for roughly 1 job in 550 and leaves it asked for the rest. Recorded rather than swapped for a better-filled field: Installation_Id__c is 98.93% filled but is Amazon\'s id, not the vendor\'s, and the destination Vendor_reference_number__c is the vendor\'s. Measured in a SANDBOX — a field written only by a production integration can read low here for reasons unrelated to the field, and this was not cross-checked against production.',
    },
  },
  {
    key: 'intervention.lockerType',
    label: 'Commercial or residential',
    fill: 1.0,
    mapping: {
      object: 'Intervention__c',
      field: 'Locker_Type__c',
      type: 'string(1300) formula',
      confidence: 'verified',
      evidence: 'discovery/salesforce/field-dumps.txt; 100% fill on actionable interventions per ' + PAYLOAD_SPEC,
      caveat: 'Formula on Intervention__c — read-only there. The writable twin is Work_Submission__c.Locker_Type__c, whose picklist mixes product line and residential/commercial values.',
    },
  },
  {
    key: 'intervention.type',
    label: 'Intervention type on the dispatch',
    fill: 0.9994,
    mapping: {
      object: 'Intervention__c',
      field: 'Intervention_Type__c',
      type: 'picklist (43 values)',
      confidence: 'verified',
      evidence:
        'discovery/salesforce/field-dumps.txt. Fill measured 25,064/25,080 = 99.94% on the actionable set (Dev1, 2026-09-03). Observed distribution on that set: Maintenance 12,885, Permit 3,350, Permanent Removal 2,406, Aesthetics 1,392, Survey 1,171, Premier Survey 889, Repair 780, Installation 779, Provisioning 337, Site Preparation 188, Survey Rework 123, and 17 further values below 100.',
    },
  },
  {
    key: 'intervention.visitKind',
    /**
     * ⚠️ `fill` DELIBERATELY ABSENT rather than restated as 1.0. This is not a field; it is a
     * derivation whose value is `undefined` for every intervention type outside the install
     * form's three options — and those types are the MAJORITY of the queue (Maintenance alone is
     * 12,885 of 25,080). Claiming a fill rate for a derivation that mostly declines to answer is
     * how the "questions removed" count inflates.
     */
    label: 'Visit kind, from the intervention',
    mapping: {
      object: 'Intervention__c',
      field: 'Intervention_Type__c + Provisioned_With_Installation__c',
      type: 'picklist (43 values) + boolean',
      confidence: 'verified',
      evidence:
        'Both fields confirmed present on the Dev1 Intervention__c describe (2026-09-03). Intervention_Type__c is 99.94% filled on the actionable set; Provisioned_With_Installation__c is a plain boolean, no formula.',
      caveat:
        '⚠️ MEASURED: Provisioned_With_Installation__c is `true` on 0 of 185,591 Dev1 rows. So the "Installation & Provisioning" branch of this derivation is UNREACHABLE against the data as it stands, and the derivation can only ever produce "Installation" or "Provisioning". The branch is implemented anyway — inferring "Installation & Provisioning" from the 43-value type picklist instead would be a guess that reroutes the whole install form. Only the two exact type values map; every other type supplies nothing and the technician is asked.',
    },
  },
  {
    key: 'intervention.dispatchReason',
    label: 'Reason for dispatch, from the intervention',
    fill: 0.5492,
    mapping: {
      object: 'Intervention__c',
      field: 'Intervention_Sub_Type__c',
      type: 'picklist, unrestricted (35 values)',
      confidence: 'verified',
      evidence:
        'discovery/salesforce/field-dumps.txt. The Aesthetic Form already receives DispatchReason as a URL parameter (discovery/qualtrics/00-SUMMARY.md §2), so this value is demonstrably available to a form at launch and re-asked anyway. Fill measured 13,775/25,080 = 54.92% on the actionable set, 83,906/185,591 = 45.21% overall, and 0/2,272 on Survey-type rows (Dev1, 2026-09-03).',
      caveat:
        '⚠️ THE PREVIOUS 1.0 WAS WRONG — measured 0.549. Blank on 45% of live jobs and on EVERY Survey-type row, so the dispatch-reason questions remain questions for most technicians. Wording also differs from the survey: the intervention says LostNetworkConnection and PowerOutage (both observed in Dev1), the dispatch-reason picklists say "Lost Network Connection" and "Power Outage". The field is unrestricted, so unexpected values do occur, and an off-list value prefilled into a single-choice question is worse than a blank — normalise, and drop anything that does not map.',
    },
  },
  {
    key: 'sitePrep.supplierChecklist',
    label: 'Site prep scoped to the supplier',
    /**
     * ⚠️ WAS `fill: 0.0` WITH THE CAVEAT "Fill rate not measured." Those are two different claims
     * and the catalog was asserting the stronger one. **Now measured, and 0.0 was wrong** — the
     * field is not empty, it is nearly unreachable, which is a different problem with a different
     * fix.
     *
     * Measured against Dev1 on 2026-09-03 (see `mapping.evidence` for the exact queries). The
     * figure recorded here is **0.0266**, on the same convention every `intervention.*` fill in
     * this file uses: the actionable population a technician's queue is drawn from, counted
     * per-intervention rather than per-row, because a value the form cannot reach is not a value
     * the form has.
     */
    fill: 0.0266,
    trusted: false,
    mapping: {
      object: 'Sim_Ticket__c',
      field: 'PI_Site_Prep_Supplier_checklist__c',
      type: 'multipicklist',
      confidence: 'verified',
      evidence:
        'discovery/salesforce/field-dumps.txt (Sim_Ticket__c describe). Its value set — Electrical Installation Outlet, Electrical Installation Disconnect, Concrete Base, Access Improvement, Installation of bollards/curb stops, plus None and one removal value — is an exact superset of the five options in Site Prep Supplier Form Q23. ' +
        'FILL MEASURED IN DEV1 2026-09-03, three denominators: (a) 713/1,416,335 = 0.05% of all Sim_Ticket__c rows; ' +
        '(b) 5/188 = 2.66% of actionable Site Preparation interventions, reached as Post_Install__r.PI_Site_Prep_Supplier_checklist__c != null; ' +
        '(c) 79/25,080 = 0.32% across the whole actionable intervention set. ' +
        'Queries: SELECT COUNT(Id) FROM Intervention__c WHERE Vendor_State__c IN (New, Scheduled, In progress, Rework, Corrective Action, Completed) AND Intervention_Type__c = \'Site Preparation\' [AND Post_Install__r.PI_Site_Prep_Supplier_checklist__c != null].',
      caveat:
        '⚠️ THE REAL BLOCKER IS REACHABILITY, NOT FILL. Only 10 of 188 actionable Site Preparation interventions have Post_Install__c set at all, so for 94.7% of site-prep jobs there is no Sim_Ticket__c to read this from — the checklist could be 100% populated and the prefill would still be blank. ' +
        '⚠️ NO PRODUCER. Not emitted by prefillInputsFromKiosk, prefillInputsFromIntervention or deviceInputs, so site-prep/checklist renders blank today. Marked `trusted: false` so it counts as ASKED in accountFor rather than inflating the prefilled total with a value nobody supplies. ' +
        'Treat an empty value as "not scoped yet" and fall back to asking. Measured in a SANDBOX; Dev1 is not production.',
    },
  },
  {
    key: 'sitePrep.readinessDate',
    label: 'Previously estimated readiness date',
    /** Measured 2026-09-03; previously carried no `fill` at all. See `mapping.evidence`. */
    fill: 0.0053,
    trusted: false,
    mapping: {
      object: 'Sim_Ticket__c',
      field: 'PI_Supplier_Site_Prep_readiness__c',
      type: 'date',
      confidence: 'verified',
      evidence:
        'discovery/salesforce/field-dumps.txt (Sim_Ticket__c describe). FILL MEASURED IN DEV1 2026-09-03: 190/1,416,335 = 0.013% of all Sim_Ticket__c rows, and 1/188 = 0.53% of actionable Site Preparation interventions reached via Post_Install__r. The actionable per-intervention figure is the one recorded, matching the convention used for every intervention.* entry above.',
      caveat:
        'The previous estimate, so showing it invites confirm-or-revise rather than re-derivation from scratch. ' +
        '⚠️ NO PRODUCER, and at 0.53% barely worth building one: bound in post-install/site_prep_readiness_date and emitted by none of the three producers, so it renders blank. Marked `trusted: false` so it counts as ASKED. Same reachability ceiling as sitePrep.supplierChecklist — it lives on the Sim_Ticket__c that 94.7% of site-prep interventions do not have. Measured in a SANDBOX.',
    },
  },
  {
    key: 'history.anchorType',
    label: 'Anchor type recorded on the last visit',
    /**
     * ⚠️ WAS `fill: 0.581`, INHERITED AND WRONG. 58.1% is the share of kiosks with **at least one
     * prior submission of any kind** — not the share whose prior submissions actually carry an
     * anchor type, which is what this key supplies. Only 19.71% of Work_Submission__c rows have
     * `Anchor_Type__c` at all, so the two numbers are measuring different things and the larger one
     * was standing in for the smaller.
     *
     * Re-measured against Dev1 on 2026-09-03: **0.149**. See `mapping.evidence` for the counts.
     */
    fill: 0.149,
    /**
     * `trusted: false` is doing two jobs, and both are honest:
     *
     *  1. **No producer.** None of the three producers emits this key, so both bindings
     *     (install/anchor_type, maintenance/anchor_type) render blank while being counted as
     *     `prefilled` — a question presented as removed that the technician still answers in full.
     *     Untrusted moves it to `asked` + `reference` in `accountFor`, which is what it is.
     *  2. **It should never have been adoptable anyway.** This is the last technician's assertion
     *     about hardware, not a reading of it, and both bindings' own `overrideHint`s say exactly
     *     that ("what the last visit recorded, not what is on the ground"). `module.serials` is
     *     already marked untrusted for the same reason — show it to compare against, never seed it
     *     as the answer.
     *
     * Whoever wires the producer should reconsider this flag deliberately at that point; that is
     * the intended cost of using it, versus a silent blank.
     */
    trusted: false,
    mapping: {
      object: 'Work_Submission__c',
      field: 'Anchor_Type__c',
      type: 'picklist Floor|Wall|Both|No',
      confidence: 'verified',
      evidence:
        'discovery/salesforce/field-dumps.txt; prior-submission history is an available read per ' + PAYLOAD_SPEC + ' §b.1 item 12, which records 58.1% of kiosks having at least one prior submission — a DIFFERENT quantity from this one. ' +
        'FILL RE-MEASURED IN DEV1 2026-09-03: 19,689/99,871 = 19.71% of Work_Submission__c rows carry Anchor_Type__c; those rows span 12,200 distinct Kiosk_ID__c values, against 81,906 OpportunityLineItem kiosks = 14.90%, and against the 46,839 distinct kiosks that appear on any submission = 26.05%. ' +
        'The recorded 0.149 is the kiosk-universe figure, because the question a prefill has to answer is "will a value exist for the kiosk I was sent to", not "given that this kiosk has history, does that history mention anchoring". ' +
        'Queries: SELECT COUNT(Id) FROM Work_Submission__c [WHERE Anchor_Type__c != null]; SELECT COUNT_DISTINCT(Kiosk_ID__c) FROM Work_Submission__c [WHERE Anchor_Type__c != null]; SELECT COUNT(Id) FROM OpportunityLineItem WHERE Kiosk_ID__c != null.',
      caveat:
        'Not kiosk truth — the last technician\'s assertion. Nothing in Salesforce records whether a locker is actually anchored (Locker_Bolting_Pics__c is 0.0% populated). Show it as "last recorded", never treat agreement as verification. Writing Anchor_Type__c = No together with Indoor_outdoor__c = Outdoor on a Maintenance record sends real outbound email through an unguarded flow, so a speculative or replayed write here has an external side effect. ' +
        '⚠️ NO PRODUCER, and it is the largest of the genuine gaps: 2 of the 6 unproduced bindings. Needs a read of the most recent Work_Submission__c for the kiosk — ORDER BY CreatedDate DESC LIMIT 1 on Kiosk_ID__c, selecting Anchor_Type__c — which no route makes today. That is a new single-kiosk history read, not a column added to the work-list projection: the work list is a bulk read of up to 100 jobs and this needs a per-kiosk sub-query.',
    },
  },
  {
    key: 'intervention.provisioningCode',
    label: 'Provisioning code',
    fill: 0.1358,
    secret: true,
    mapping: {
      object: 'Intervention__c',
      field: 'Provisioning_Code__c',
      type: 'string(1300) formula',
      confidence: 'verified',
      evidence:
        'discovery/salesforce/field-dumps.txt; fill re-measured 3,407/25,080 = 13.58% on the actionable set (Dev1, 2026-09-03), corroborating the 0.142 previously recorded.',
      caveat:
        'Treat as a secret. Never log it, never put it in an error message, never send it to analytics. ' +
        '⚠️ NO PRODUCER — DELIBERATE, and the only one of the six unproduced bindings that is. Absent from INTERVENTION_LIST_FIELDS on purpose: the work list is a bulk read of up to 100 jobs, and putting a live provisioning code in every row of it — cached nowhere, but present in one response and one log-adjacent object — is a wider exposure than fetching it for the one job being worked. ' +
        'What it needs is therefore NOT a column on the work-list projection but a single-intervention route (GET /interventions/{id}) that returns the code only for the job being opened. Until that exists install/provisioning_code and provisioning/provisioning_code render blank. ' +
        'Left `trusted` rather than flagged untrusted, unlike the four genuine gaps: the value IS adoptable once it arrives, and downgrading it would misdescribe a security decision as a data-quality one. ACCOUNTING CONSEQUENCE, stated rather than hidden: these 2 bindings are still counted as `prefilled` by accountFor while rendering blank.',
    },
  },
  {
    key: 'intervention.warehouse',
    label: 'Return warehouse',
    fill: 0.0905,
    mapping: {
      object: 'Intervention__c',
      field: 'Ware_House_Location__c',
      type: 'reference to Location',
      confidence: 'verified',
      evidence:
        'Named in ' + PAYLOAD_SPEC + ' §b.1 item 15 as Ware_House_Location__r.Name. CONFIRMED on the Dev1 Intervention__c describe (2026-09-03): the field exists, type `reference`, referenceTo `Location`. Fill measured 2,269/25,080 = 9.05% on the actionable set, 3,000/185,591 = 1.62% overall.',
      caveat:
        'No producer. Not in INTERVENTION_LIST_FIELDS and bound to no question: at 9% it removes roughly one question in eleven, and the useful value is Ware_House_Location__r.Name rather than the 18-char id this field holds, which needs a relationship hop the work-list projection does not make.',
    },
  },
  {
    key: 'case.number',
    label: 'Case number',
    fill: 1.0,
    mapping: {
      object: 'Case',
      field: 'CaseNumber',
      type: 'string',
      confidence: 'verified',
      evidence: KIOSK_READ + ' (Case__r subselect)',
    },
  },
  {
    key: 'case.installationMethod',
    label: 'Planned installation method',
    fill: 0.079,
    mapping: {
      object: 'Case',
      field: 'Installation_Method__c',
      type: 'picklist (6 values)',
      confidence: 'verified',
      evidence: KIOSK_READ + ' (Case__r subselect); discovery/salesforce/field-dumps.txt',
      caveat: 'Case has 6 values; Work_Submission__c.Installation_Method__c has 10. A normalisation map is required before this can be written back.',
    },
  },
  {
    key: 'case.hostSitePrepChecklist',
    label: 'Site prep the host was asked to do',
    fill: 0.158,
    mapping: {
      object: 'Case',
      field: 'Host_Site_Prep_Checklist__c',
      type: 'multipicklist(4099)',
      confidence: 'unverified',
      evidence: 'Named in ' + PAYLOAD_SPEC + ' §b.2. Not seen in the Case describe excerpt reviewed; the confirmed neighbour is Sim_Ticket__c.PI_Host_Site_Prep_Checklist__c.',
    },
  },
  {
    key: 'case.riskLevel',
    label: 'Site risk level',
    fill: 0.999,
    mapping: {
      object: 'Case',
      field: 'Risk_Level__c',
      type: 'string(1300) formula',
      confidence: 'verified',
      evidence: KIOSK_READ + ' (Case__r subselect)',
      caveat: '98% reads LOW. Surface only when it is not LOW, otherwise it is noise. Prefer this over OpportunityLineItem.Kiosk_Security_Risk__c, which returns raw HTML.',
    },
  },
  {
    key: 'case.parkingRestrictions',
    label: 'Parking and unloading restrictions',
    fill: 0.011,
    mapping: {
      object: 'Case',
      field: 'Parking_unloading_restrictions__c',
      type: 'string(255)',
      confidence: 'verified',
      evidence: KIOSK_READ + ' (Case__r subselect)',
    },
  },

  /* ---- modules: real data, untrustworthy content ---- */
  {
    key: 'module.serials',
    label: 'Module serials on the asset records',
    fill: 0.669,
    trusted: false,
    mapping: {
      object: 'SerializedProduct',
      field: 'SerialNumber',
      type: 'string(255)',
      confidence: 'verified',
      evidence: 'discovery/salesforce/raw/desc_dev1-sandbox_SerializedProduct.json; coverage measured in ' + PAYLOAD_SPEC + ' §b.1 item 8',
      caveat:
        'Untrusted by design. Roughly 60% of stored serials are synthetic migration placeholders and 1 in 3 kiosks has no module rows at all. Shown to compare against, never adopted as the answer — prefilling a placeholder as though a human read the label is exactly the fabrication this app must not do. ' +
        '⚠️ NO PRODUCER: SerializedProduct rows are not in the reduced kiosk payload at all (see the header of compose/kiosk.ts), so the 20 install bindings render no reference value today. Already `trusted: false`, so the accounting has always counted all 20 as asked — which is why these 20 are excluded from the six-gap count.',
    },
  },
] as const;

const BY_KEY = new Map(PREFILL_SOURCES.map((spec) => [spec.key, spec]));

export function prefillSource(key: string): PrefillSourceSpec | undefined {
  return BY_KEY.get(key);
}

/**
 * Whether a source may be adopted as an answer.
 *
 * An unknown key is treated as untrusted rather than trusted, so a typo in a schema reduces the
 * prefill count instead of quietly inflating it.
 */
export function isTrustedSource(key: string): boolean {
  const spec = BY_KEY.get(key);
  return spec !== undefined && spec.trusted !== false;
}

export const PREFILL_KEYS: ReadonlySet<string> = new Set(PREFILL_SOURCES.map((s) => s.key));

/**
 * Which keys something actually fetches, as of 2026-09-03.
 *
 * A prefill binding with no producer is the quietest failure this catalog can have: the schema
 * claims a question is answered, `accountFor` counts it as removed, and the technician sees an
 * empty box. Nothing else in the app can notice, because "the value did not arrive" is
 * indistinguishable from "the record was blank".
 *
 * So the producer set is declared here — in the file that already claims to be "the single place a
 * Salesforce read is described" — and `test/forms/schema-defects.test.ts` asserts that every bound
 * key is either in it or is `trusted: false` with `NO PRODUCER` in its caveat. Adding a binding to
 * an unproduced key then fails the build instead of shipping a blank field.
 *
 * Transcribed from the three producers, which are read-only from here (another agent owns
 * `src/compose`), so this list is a mirror and can drift. The test's job is to make the drift
 * expensive in one direction only: a new binding to something unlisted fails. A producer that
 * starts emitting a new key does not fail, and does not need to.
 *
 *  · `compose/kiosk.ts`        → `prefillInputsFromKiosk`
 *  · `compose/intervention.ts` → `prefillInputsFromIntervention`
 *  · `compose/device.ts`       → `deviceInputs`
 */
export const PRODUCED_PREFILL_KEYS: ReadonlySet<string> = new Set([
  // compose/device.ts
  'visit.startTime',
  // compose/kiosk.ts
  'kiosk.id',
  'kiosk.name',
  'kiosk.status',
  'kiosk.generation',
  'kiosk.color',
  'kiosk.accessPointType',
  'kiosk.mountType',
  'kiosk.placement',
  'kiosk.layout',
  'kiosk.totalSlots',
  'kiosk.macAddress',
  'kiosk.siteName',
  'kiosk.street',
  'kiosk.countryCode',
  'kiosk.directions',
  'kiosk.accessMechanism',
  'kiosk.floorType',
  'kiosk.contactEmail',
  'kiosk.contactMobile',
  'case.number',
  'case.installationMethod',
  'case.parkingRestrictions',
  'case.riskLevel',
  // compose/intervention.ts
  'intervention.id',
  'intervention.type',
  'intervention.visitKind',
  'intervention.lockerType',
  'intervention.locationName',
  'intervention.installationId',
  'intervention.postInstallId',
  'intervention.dispatchReason',
  'intervention.supplierWorkOrderId',
]);

/** Raw values fetched for one visit, keyed by catalog key. */
export type PrefillInputs = Readonly<Record<string, Answer | undefined>>;

export interface ResolvedPrefill {
  readonly value: Answer;
  readonly spec: PrefillSourceSpec;
  readonly fetchedAt: number;
}

/**
 * Turns a fetched payload into the bundle `createState` seeds from.
 *
 * A key with no catalog entry is dropped rather than guessed at, and the caller is told which
 * keys were unknown — a silently ignored prefill is how a form quietly starts asking questions
 * it does not need to.
 */
export function resolvePrefill(
  inputs: PrefillInputs,
  fetchedAt: number,
): { bundle: Record<string, ResolvedPrefill>; unknownKeys: string[] } {
  const bundle: Record<string, ResolvedPrefill> = {};
  const unknownKeys: string[] = [];

  for (const [key, value] of Object.entries(inputs)) {
    const spec = BY_KEY.get(key);
    if (!spec) {
      unknownKeys.push(key);
      continue;
    }
    if (value === undefined || value === null) continue;
    if (typeof value === 'string' && value.trim() === '') continue;
    bundle[key] = { value, spec, fetchedAt };
  }

  return { bundle, unknownKeys };
}

/**
 * Context values derived from prefill, for display logic that must not be a question.
 *
 * `lockerPlatform` is the highest-leverage derivation in the app: CORE versus ODIN gates
 * roughly 40 of the Maintenance survey's 124 questions, and `Access_Point_Type__c` is 100%
 * populated, so the platform is knowable without asking. The mapping from product line to
 * platform is stated explicitly here rather than buried in a schema so it can be reviewed.
 */
export function deriveContext(inputs: PrefillInputs): Record<string, string | number | boolean> {
  const generation = String(inputs['kiosk.generation'] ?? '');
  const accessPointType = String(inputs['kiosk.accessPointType'] ?? '');
  const layout = String(inputs['kiosk.layout'] ?? '');

  const odinByType = /odin/i.test(accessPointType);
  const odinByGeneration = /^(4|5)/.test(generation);

  const moduleCountRaw = inputs['kiosk.moduleCount'];
  const moduleCount =
    typeof moduleCountRaw === 'number' && Number.isFinite(moduleCountRaw)
      ? moduleCountRaw
      : layout.length > 0
        ? layout.length
        : 0;

  return {
    generation,
    accessPointType,
    countryCode: String(inputs['kiosk.countryCode'] ?? ''),
    placement: String(inputs['kiosk.placement'] ?? ''),
    mountType: String(inputs['kiosk.mountType'] ?? ''),
    lockerType: String(inputs['intervention.lockerType'] ?? ''),
    interventionType: String(inputs['intervention.type'] ?? ''),
    lockerPlatform: odinByType || odinByGeneration ? 'ODIN' : 'CORE',
    moduleCount,
    // Number of joins between adjacent columns: one fewer than the column count.
    intermediateAnchorCount: Math.max(0, moduleCount - 1),
    hasProvisioningCode: Boolean(inputs['intervention.provisioningCode']),
  };
}
